import { z } from "zod"
import { type TokenMetadataType, TokenMetadataSchema, TokenSwapFeeTypeEnum } from "@tutellus/core-contracts-ifaces"

const ProjectConfigTokenMetadataValidation = z.object({
  permitted: z.boolean({ required_error: "permitted is required" }),
  domainVersion: z.string({ required_error: "domainVersion is required" }),
  swapFeeType: z.nativeEnum(TokenSwapFeeTypeEnum),
  domainName: z.string({ required_error: "domainName is required" }).optional(),
})

export class ProjectConfigTokenMetadata {
  constructor(
    private readonly _permitted: boolean,
    private readonly _domainVersion: string,
    private readonly _swapFeeType: TokenSwapFeeTypeEnum,
    private readonly _domainName?: string,
  ) {}

  static create({
    permitted,
    domainName,
    domainVersion,
    swapFeeType,
  }: z.infer<typeof ProjectConfigTokenMetadataValidation>) {
    const { success, error } = ProjectConfigTokenMetadataValidation.safeParse({
      permitted,
      domainName,
      domainVersion,
      swapFeeType,
    })
    if (!success) throw new Error(error.message)
    if (permitted && !domainName) throw new Error("Missing domain name")
    return new ProjectConfigTokenMetadata(permitted, domainVersion, swapFeeType, domainName ?? undefined)
  }

  static createFromAPI({ permitted, domainName, domainVersion, swapFeeType }: TokenMetadataType) {
    const { success, error } = TokenMetadataSchema.safeParse({ permitted, domainName, domainVersion, swapFeeType })
    if (!success) throw new Error(error.message)
    return ProjectConfigTokenMetadata.create({
      permitted,
      domainName: domainName ?? undefined,
      domainVersion,
      swapFeeType,
    })
  }

  get permitted() { return this._permitted } // prettier-ignore
  get domainName() { return this._domainName } // prettier-ignore
  get domainVersion() { return this._domainVersion } // prettier-ignore
  get swapFeeType() { return this._swapFeeType } // prettier-ignore

  serialize() {
    return {
      permitted: this._permitted,
      domainName: this._domainName,
      domainVersion: this._domainVersion,
      swapFeeType: this._swapFeeType,
    }
  }
}
